import React, { useEffect, useMemo, useState } from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { DataProviderInstance } from '../../../../api/DataProvider';
import useDayRooms from '../../../../components/day-rooms/useDayRooms';
import DayRoomChip from '../../../../components/day-rooms/DayRoomChip';
import DayRoomPopup from '../../../../components/day-rooms/DayRoomPopup';
import { findRoomConflicts, summarizeDayRooms, toDayKey } from '../../../../components/day-rooms/dayRooms';
import styles from './DayRooms.module.css';

const NOTICE_MS = 6000;

/**
 * "Salas de hoje": em que sala cada agenda atende hoje (ou amanhã). O chip abre o
 * popup que troca a sala só daquele dia, o dia todo ou a partir de um horário.
 * Quem renderiza decide se o card aparece (clínica com salas, usuário que vê
 * todas as agendas).
 */
function DayRoomsCard() {
  const { CompanyConfig } = DataProviderInstance();
  const [dayOffset, setDayOffset] = useState(0); // 0 = hoje, 1 = amanhã
  const [editing, setEditing] = useState(null); // agenda aberta no popup
  const [notice, setNotice] = useState('');

  const date = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    return toDayKey(d);
  }, [dayOffset]);

  const { assignments, reload } = useDayRooms(date);
  const rooms = useMemo(() => CompanyConfig?.Rooms || [], [CompanyConfig?.Rooms]);
  const resources = useMemo(
    () => (CompanyConfig?.Resources || []).filter((r) => r.Active == null || Number(r.Active) === 1),
    [CompanyConfig?.Resources],
  );
  const summaries = useMemo(() => summarizeDayRooms(assignments, resources, rooms), [assignments, resources, rooms]);
  const conflicts = useMemo(() => findRoomConflicts(summaries, rooms), [summaries, rooms]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const handleApplied = ({ changed, roomLabel, startTime }) => {
    reload();
    const destino = roomLabel || 'sem sala';
    const quando = startTime && startTime !== '00:00' ? ` a partir das ${startTime}` : '';
    setNotice(changed > 0
      ? `${changed} ${changed === 1 ? 'agendamento passou' : 'agendamentos passaram'} para ${destino}${quando}.`
      : `Sala definida: ${destino}${quando}. Os próximos agendamentos já nascem nela.`);
  };

  if (resources.length === 0) return null;

  return (
    <section className={styles.card} aria-label="Salas do dia">
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>{dayOffset === 0 ? 'Salas de hoje' : 'Salas de amanhã'}</h2>
          <p className={styles.subtitle}>Onde cada agenda atende. Toque na sala para trocar só neste dia.</p>
        </div>
        <button type="button" className={styles.dayToggle} onClick={() => setDayOffset(dayOffset === 0 ? 1 : 0)}>
          {dayOffset === 0 ? 'Ver amanhã' : 'Ver hoje'}
        </button>
      </header>

      <ul className={styles.list}>
        {summaries.map((summary) => (
          <li key={summary.resourceId} className={styles.row}>
            <span className={styles.resourceLabel} title={summary.resourceLabel}>{summary.resourceLabel}</span>
            <DayRoomChip
              summary={summary}
              onClick={() => setEditing(resources.find((r) => String(r.Id) === summary.resourceId) || null)}
            />
          </li>
        ))}
      </ul>

      {conflicts.length > 0 && (
        <div className={styles.conflicts}>
          <WarningAmberOutlinedIcon className={styles.conflictIcon} />
          <div className={styles.conflictText}>
            {conflicts.map((c) => (
              <p key={c.roomLabel} className={styles.conflictLine}>
                {c.roomLabel} está em {c.resourceLabels.length} agendas: {c.resourceLabels.join(', ')}.
              </p>
            ))}
          </div>
        </div>
      )}

      {notice && <p className={styles.notice}>{notice}</p>}

      {editing && (
        <DayRoomPopup
          resource={editing}
          date={date}
          rooms={rooms}
          assignments={assignments}
          onClose={() => setEditing(null)}
          onApplied={handleApplied}
        />
      )}
    </section>
  );
}

export default DayRoomsCard;
